<template>
  <div>
    <b-modal
      hide-footer
      hide-backdrop
      no-close-on-esc
      title="Workstation"
      ref="workStationSettingModal"
      id="workStationSettingModal"
      content-class="workstation-modal"
      body-class="workstation-modal-body"
      header-class="workstation-modal-header"
      size="md"
      @shown="loadWorkstation"
    >
      <p class="ws-help">
        Choose how this POS terminal should operate.
      </p>

      <div class="ws-options">
        <button
          type="button"
          v-for="option in workstationOptions"
          :key="option.value"
          class="ws-option"
          :class="{ active: selectedWorkstation === option.value }"
          @click="selectedWorkstation = option.value"
        >
          <span class="ws-option-radio" aria-hidden="true"></span>
          <span class="ws-option-text">
            <span class="ws-option-title">{{ option.label }}</span>
            <span class="ws-option-sub">{{ option.description }}</span>
          </span>
        </button>
      </div>

      <div class="ws-hardware">
        <label class="ws-hardware-label" for="ws_hardware_name">
          Hardware Name
        </label>
        <input
          id="ws_hardware_name"
          v-model="hardwareName"
          class="ws-hardware-input"
          type="text"
          maxlength="50"
          placeholder="POS-XXXXXX"
          autocomplete="off"
        />
        <small class="ws-hardware-hint">
          Identifies this terminal on receipts and reports. Kept on this machine
          and not cleared on logout.
        </small>
      </div>

      <div class="ws-actions">
        <button
          class="btn-blue ws-btn-primary"
          :disabled="!selectedWorkstation || isSaving"
          @click="saveWorkstation"
        >
          {{ isSaving ? "Saving…" : "Apply" }}
        </button>
        <button
          type="button"
          class="ws-btn-secondary"
          :disabled="isSaving"
          @click.prevent.stop="$bvModal.hide('workStationSettingModal')"
        >
          Cancel
        </button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import {
  getWorkstation,
  saveWorkstation as saveWorkstationSetting,
  getWorkstationHomePath,
  getHardwareName,
  saveHardwareName,
} from "@/util/indexDb/workstationIndexDb";

export default {
  data() {
    return {
      isSaving: false,
      selectedWorkstation: "",
      hardwareName: "",
      workstationOptions: [
        {
          value: "BO",
          label: "BO",
          description: "Box Office — tickets only",
        },
        {
          value: "FNB",
          label: "FNB",
          description: "Food & Beverage only",
        },
        {
          value: "BOFNB",
          label: "BO + FNB",
          description: "Tickets and Food & Beverage",
        },
        {
          value: "GAMES",
          label: "Games",
          description: "Games / bowling concessions",
        },
        {
          value: "BOFNBGAMES",
          label: "BO + FNB + Games",
          description: "Tickets, F&B, and Games",
        },
      ],
    };
  },
  mounted() {
    this.loadWorkstation();
  },
  methods: {
    async loadWorkstation() {
      try {
        const value = await getWorkstation();
        const allowed = this.workstationOptions.map((o) => o.value);
        if (allowed.includes(value)) {
          this.selectedWorkstation = value;
        }
      } catch (error) {
        console.log("Unable to load workstation from IndexedDB", error);
      }

      try {
        // Mints a POS-XXXXXX name the first time this terminal is opened.
        this.hardwareName = await getHardwareName();
      } catch (error) {
        console.log("Unable to load hardware name from IndexedDB", error);
      }
    },
    async saveWorkstation() {
      if (!this.selectedWorkstation) {
        this.$toasted.show("Please select a workstation.");
        return;
      }

      try {
        this.isSaving = true;
        await saveWorkstationSetting(this.selectedWorkstation);
        // Blank falls back to a freshly generated POS-XXXXXX rather than no name at all.
        this.hardwareName = await saveHardwareName(this.hardwareName);
        this.$toasted.show("Workstation saved successfully.");
        this.$bvModal.hide("workStationSettingModal");

        // Land on the correct module for this workstation (e.g. BO → Schedules)
        const homePath = getWorkstationHomePath(this.selectedWorkstation);
        if (homePath && this.$route && this.$route.path !== homePath) {
          this.$router.push({ path: homePath });
        }
      } catch (error) {
        console.log("Unable to save workstation in IndexedDB", error);
        this.$toasted.show("Unable to save workstation.");
      } finally {
        this.isSaving = false;
      }
    },
  },
};
</script>

<style>
#workStationSettingModal___BV_modal_content_ {
  width: 560px !important;
  max-width: 94vw;
  margin: auto;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.18);
}

#workStationSettingModal___BV_modal_header_ {
  border-bottom: 1px solid #e8e8e8;
  padding: 16px 20px 12px;
}

#workStationSettingModal___BV_modal_title_ {
  width: 100%;
  text-align: left;
  color: var(--blue);
  font-weight: 700;
  font-size: 18px;
  margin: 0;
}

.workstation-modal-body {
  padding: 12px 20px 20px !important;
}

.ws-hardware {
  display: flex;
  flex-direction: column;
  margin: 16px 0 4px;
}

.ws-hardware-label {
  font-size: 13px;
  font-weight: 600;
  color: #333;
  margin-bottom: 6px;
}

.ws-hardware-input {
  border: 1px solid #dcdcdc;
  border-radius: 6px;
  padding: 9px 12px;
  font-size: 14px;
  outline: none;
}

.ws-hardware-input:focus {
  border-color: var(--blue);
}

.ws-hardware-hint {
  margin-top: 6px;
  font-size: 12px;
  color: #888;
  line-height: 1.4;
}

.ws-help {
  margin: 0 0 14px;
  font-size: 13px;
  line-height: 1.45;
  color: #666;
  font-weight: 400;
}

.ws-options {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin-bottom: 16px;
}

.ws-option {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  width: 100%;
  text-align: left;
  border: 1.5px solid #e2e2e2;
  background: #fff;
  border-radius: 10px;
  padding: 12px;
  cursor: pointer;
  min-height: 72px;
  transition: border-color 0.15s ease, background 0.15s ease,
    box-shadow 0.15s ease;
}

.ws-option:hover {
  border-color: var(--blue);
  background: #f7faff;
}

.ws-option.active {
  border-color: var(--blue);
  background: #eef4ff;
  box-shadow: 0 0 0 1px var(--blue);
}

.ws-option-radio {
  flex: 0 0 auto;
  width: 18px;
  height: 18px;
  margin-top: 2px;
  border-radius: 50%;
  border: 2px solid #b0b0b0;
  position: relative;
  background: #fff;
}

.ws-option.active .ws-option-radio {
  border-color: var(--blue);
}

.ws-option.active .ws-option-radio::after {
  content: "";
  position: absolute;
  top: 50%;
  left: 50%;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--blue);
  transform: translate(-50%, -50%);
}

.ws-option-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.ws-option-title {
  font-size: 14px;
  font-weight: 700;
  color: #222;
  line-height: 1.3;
}

.ws-option.active .ws-option-title {
  color: var(--blue);
}

.ws-option-sub {
  font-size: 11px;
  font-weight: 400;
  color: #777;
  line-height: 1.35;
}

.ws-actions {
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  align-items: center;
  padding-top: 4px;
}

.ws-btn-primary {
  min-width: 100px;
}

.ws-btn-secondary {
  min-width: 90px;
  height: 36px;
  padding: 0 16px;
  border-radius: 6px;
  border: 1.5px solid #c8c8c8;
  background: #fff;
  color: #444;
  font-weight: 600;
  font-size: 13px;
  cursor: pointer;
}

.ws-btn-secondary:hover:not(:disabled) {
  border-color: #999;
  background: #f5f5f5;
}

.ws-btn-secondary:disabled,
.ws-btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
</style>



// WEBPACK FOOTER //
// src/components/Partials/WorkstationPopup.vue